import { Component, OnInit, ElementRef, inject, Injector, runInInjectionContext, signal, computed, AfterViewInit } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { Location } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { ActivityService } from 'src/app/shared/activity.service';

@Component({
	selector: 'app-books',
	templateUrl: './books.component.html',
	styleUrls: ['./books.component.scss'],
	standalone: false // Keeping the traditional NgModule architecture
})
export class BooksComponent implements OnInit, AfterViewInit {

	// Modern Dependency Injection
	private readonly afs = inject(AngularFirestore);
	private readonly auth = inject(ActivityService);
	public readonly storage = inject(AngularFireStorage);
	private readonly el = inject(ElementRef);
	private readonly location = inject(Location);
	private readonly injector = inject(Injector);

	// Signals for Reactive State
	readonly loading = signal(true);
	readonly tabs = signal<any[][]>([]);
	readonly activeWindow = signal(1);

	// Computed Signals
	readonly tabCount = computed(() => this.tabs().length);
	readonly styleLeftPx = computed(() => this.tabs().length * 11);

	userInfo: any;

	async ngOnInit() {
		const adminUserData = JSON.parse(localStorage.getItem('quranera_admin_userData') || 'null');

		try {
			// Fetch user data
			const childData: any = await firstValueFrom(
				this.auth.getSelfData({ collectionID: "userData", token: localStorage.getItem("accessToken") })
			);
			this.userInfo = childData?.result;

			// Fetch activity lists
			const listsSnapshot = await runInInjectionContext(this.injector, () => {
				return firstValueFrom(this.afs.collection('activity-lists').get());
			});

			let allBooks: any[] = [];

			// Fetch sub-collections in parallel
			const fetchPromises = listsSnapshot.docs.map(async (listDoc) => {
				const docId = listDoc.id;

				const gamesSnapshot = await runInInjectionContext(this.injector, () => {
					return firstValueFrom(
						this.afs.collection('activity-lists').doc(docId)
							.collection('activities', ref => ref.where('type', '==', 'reading')).get()
					);
				});

				const listGames: any[] = [];

				gamesSnapshot.forEach(game => {
					const gameData: any = game.data();
					let url;
					let lock = false;

					if (adminUserData || this.userInfo?.activities?.[docId]?.[gameData.orderNumber]) {
						// Updated routing URL for reading activities
						url = ['/game/activity-reading', docId, gameData.orderNumber];
					} else {
						lock = true;
					}

					const docData: any = listDoc.data();

					listGames.push({
						countryTitle: docData.countryTitle,
						type: gameData.type,
						title: gameData.title,
						url,
						lock
					});
				});

				return listGames;
			});

			// Wait for all fetches to complete, then flatten the array
			const results = await Promise.all(fetchPromises);
			results.forEach(books => {
				allBooks = allBooks.concat(books);
			});

			this.createArray(allBooks);

		} catch (error) {
			console.error("Error loading books data:", error);
			this.loading.set(false);
		}
	}

	private createArray(books: any[]) {
		const generatedTabs = [];

		// Books component groups by 10 items per tab
		const count = Math.ceil(books.length / 10);

		for (let i = 0; i < count; i++) {
			generatedTabs.push(books.slice(i * 10, (i * 10) + 10));
		}

		this.tabs.set(generatedTabs);
		this.loading.set(false);
	}

	backToMenu() {
		this.previousState();
	}

	ngAfterViewInit(): void {
		// Event Delegation: Attach ONE listener to the component host instead of multiple listeners to every button.
		// This natively handles dynamically rendered DOM elements without needing `setTimeout`.

		this.el.nativeElement.addEventListener('click', (event: MouseEvent) => {
			const target = event.target as HTMLElement;
			if (target.closest('button') || target.closest('.item')) {
				const audio = document.getElementById('buttonClick') as HTMLAudioElement;
				if (audio) audio.play().catch(e => console.warn('Audio blocked by browser', e));
			}
		});

		this.el.nativeElement.addEventListener('mouseover', (event: MouseEvent) => {
			const target = event.target as HTMLElement;
			if (target.closest('button') || target.closest('.item')) {
				const audio = document.getElementById('buttonHover') as HTMLAudioElement;
				if (audio) audio.play().catch(e => console.warn('Audio blocked by browser', e));
			}
		});
	}

	previousState() {
		this.location.back();
	}

	previous() {
		if (this.activeWindow() > 1) {
			this.activeWindow.update(val => val - 1);
		}
	}

	next() {
		if (this.activeWindow() < this.tabCount()) {
			this.activeWindow.update(val => val + 1);
		}
	}
}
